import * as React from 'react';
import {styled, themeable, forwardHtmlProps} from '@packages/utilities/styled';
import {
  checkboxStyle,
  checkboxStyleVariations,
  checkmarkStyle,
  checkmarkStyleVariations,
} from './CheckboxStyle';

const CheckmarkIcon: React.ComponentType<{
  hover?: boolean;
  checked?: boolean;
  dashed?: boolean;
  accent?: boolean;
}> = themeable((props) => {
  const Icon = props.dashed
    ? props.theme.checkbox.icons.dashmark
    : props.theme.checkbox.icons.checkmark;

  const htmlProps = forwardHtmlProps(props, 'svg');
  return <Icon {...htmlProps} />;
});

const primaryCheckboxShadow = {
  filter: `drop-shadow(0px 0px 1px rgba(0, 0, 0, .3)) drop-shadow(0px 1px .4px rgba(0, 0, 0, .3))`,
} as const;

const enhancedCheckmarkStyles = (props: any) => ({
  ...checkmarkStyle(props),
  filter: props.intent === 'primary' ? primaryCheckboxShadow.filter : null,
});

export const Checkmark = styled(CheckmarkIcon)(
  enhancedCheckmarkStyles,
  checkmarkStyleVariations
);

const IconWrapper = styled('div')(checkboxStyle, checkboxStyleVariations);

export function CheckboxCheckmark(props: any) {
  const {
    accent,
    checked,
    hover,
    indeterminate,
    intent,
    label,
    showPreviewCheck,
    sizing,
    ...rest
  } = props;

  /**
   * @TODO Remove this from rendering (system-wide) when possible.
   * 2021-09-14
   *
   * The [label=""] attribute is relied on a lot at the moment, specifically for
   * Cypress E2E test, both as selectors and as assertions.
   *
   * Ideally, we shouldn't be using the `label` in this way.
   *
   * Labels should be provided with:
   * - aria-label attribute
   * - aria-labelledby attribute
   * - <label /> element
   *
   * As a workaround...
   * The follow enables the rendering of the HTML label attribute if the
   * label is a string. Otherwise, it will result in [object Object].
   */
  const labelAttribute = typeof label === 'string' ? label : undefined;

  return (
    <IconWrapper
      accent={accent}
      checked={checked}
      dashed={indeterminate}
      intent={intent === 'primary'}
      labelled={Boolean(label)}
      label={labelAttribute}
      sizing={sizing}
      {...rest}
    >
      <Checkmark
        accent={accent}
        checked={checked}
        dashed={indeterminate}
        hover={hover}
        intent={intent}
        showPreviewCheck={showPreviewCheck}
      />
    </IconWrapper>
  );
}

export default CheckboxCheckmark;
